import React from 'react';
import { Box, Skeleton } from '@mui/material';

interface BarChartSkeletonProps {
  height?: number;
  barWidth?: number;
  maxBarHeight?: number;
  barCount?: number;
  orientation?: 'vertical' | 'horizontal';
}

export const BarChartSkeleton: React.FC<BarChartSkeletonProps> = ({
  height = 420,
  barWidth = 40,
  maxBarHeight = 220,
  barCount = 12,
  orientation = 'vertical',
}) => {
  const isVertical = orientation === 'vertical';

  const values = Array.from({ length: barCount }).map((_, i) => {
    const scale = [0.4, 0.55, 0.7, 0.85, 1, 0.6, 0.75][i % 7];
    return maxBarHeight * scale;
  });

  return (
    <Box
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        p: 4,
        height,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ minWidth: 0 }}>
        <Skeleton variant="text" width="40%" height={28} />
      </Box>

      <Box mt={3} mb={1} display="flex" alignItems="center" justifyContent="center" gap={4} sx={{ minWidth: 0 }}>
        {[1, 2, 3].map((key) => (
          <Box key={key} display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
            <Skeleton variant="rectangular" width={10} height={10} />
            <Skeleton variant="text" width={80} height={22} />
          </Box>
        ))}
      </Box>

      <Box sx={{ flex: 1, display: 'flex', minWidth: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: isVertical ? 'column' : 'row',
            p: 4,
            boxSizing: 'border-box',
          }}
        >
          {!isVertical && (
            <Box
              sx={{
                flex: '0 0 auto',
                width: 96,
                maxWidth: 96,
                minWidth: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                mr: 2,
              }}
            >
              {Array.from({ length: barCount }).map((_, i) => (
                <Skeleton key={i} variant="text" width="100%" height={20} />
              ))}
            </Box>
          )}

          {!isVertical && <Skeleton variant="rectangular" width={3} height="100%" sx={{ flex: '0 0 auto', mr: 2 }} />}

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: isVertical ? 'row' : 'column',
              alignItems: isVertical ? 'flex-end' : 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              pb: isVertical ? 1 : 0,
            }}
          >
            {values.map((v, i) => {
              const pct = Math.max(12, Math.round((v / maxBarHeight) * 100));
              return (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  sx={
                    isVertical
                      ? {
                        flex: '1 1 0',
                        minWidth: 8,
                        maxWidth: barWidth,
                        height: v,
                      }
                      : {
                        height: barWidth,
                        width: `${pct}%`,
                        maxWidth: '100%',
                      }
                  }
                />
              );
            })}
          </Box>

          {isVertical && (
            <>
              <Skeleton variant="rectangular" height={3} width="100%" sx={{ mt: 1 }} />
              <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', gap: 1, minWidth: 0, overflow: 'hidden' }}>
                {Array.from({ length: barCount }).map((_, i) => (
                  <Skeleton key={i} variant="text" sx={{ flex: '1 1 0', minWidth: 20, maxWidth: 60 }} height={16} />
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};